import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const SOCIALS = [
  { icon: Instagram, label: "Instagram", testid: "social-instagram" },
  { icon: Facebook, label: "Facebook", testid: "social-facebook" },
  { icon: Twitter, label: "Twitter", testid: "social-twitter" },
  { icon: Youtube, label: "YouTube", testid: "social-youtube" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [joining, setJoining] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.categories().then(setCategories).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setJoining(true);
    try {
      await api.subscribe(email);
      toast.success("Welcome aboard — check your inbox for a hello from us.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe right now. Please try again.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-neutral-950 text-neutral-300">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-8 border-b border-white/10 py-16 sm:py-20 lg:grid-cols-2" data-testid="newsletter-section">
          <Reveal>
            <h2 className="font-serif text-3xl font-medium text-white sm:text-4xl">
              Make Everyday Shopping <span className="italic">Simpler.</span>
            </h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-neutral-400">
              Subscribe for exclusive discounts, early access to new drops, and practical tips for everyday living.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <form onSubmit={subscribe} className="flex flex-col gap-3 sm:flex-row" data-testid="newsletter-form">
              <input
                data-testid="newsletter-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="h-12 flex-1 rounded-full border border-white/15 bg-white/5 px-5 text-sm text-white outline-none transition-colors placeholder:text-neutral-500 focus:border-white/40"
              />
              <button
                data-testid="newsletter-join-button"
                type="submit"
                disabled={joining}
                className="group inline-flex h-12 items-center justify-center gap-2 rounded-full bg-white px-7 text-sm font-semibold text-neutral-900 transition-colors hover:bg-neutral-200 disabled:opacity-60"
              >
                {joining ? "Joining..." : "Join Us"}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 gap-10 py-14 md:grid-cols-4 lg:grid-cols-5">
          <div className="col-span-2 lg:col-span-2">
            <Link to="/" data-testid="footer-logo" className="whitespace-nowrap font-logo text-xl font-bold uppercase leading-none tracking-[0.045em] text-white">
              Everyday Essentials
              <sup className="ml-0.5 align-super text-[9px] font-medium tracking-normal text-neutral-400">®</sup>
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-neutral-400">
              Practical, useful, and affordable products designed to make everyday living easier and more beautiful.
            </p>
            <div className="mt-6 flex gap-2">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  data-testid={s.testid}
                  aria-label={s.label}
                  className="grid h-9 w-9 place-items-center rounded-full border border-white/15 text-neutral-400 transition-all duration-300 hover:border-white hover:text-white"
                >
                  <s.icon size={15} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500">Shop</h3>
            <ul className="mt-4 space-y-2.5">
              {categories.slice(0, 5).map((c) => (
                <li key={c.slug}>
                  <Link to={`/shop?category=${c.slug}`} className="text-sm text-neutral-400 transition-colors hover:text-white" data-testid={`footer-shop-${c.slug}`}>
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500">Customer Care</h3>
            <ul className="mt-4 space-y-2.5">
              <li><Link to="/policies/shipping" className="text-sm text-neutral-400 transition-colors hover:text-white" data-testid="footer-shipping-policy">Shipping Policy</Link></li>
              <li><Link to="/policies/refund" className="text-sm text-neutral-400 transition-colors hover:text-white" data-testid="footer-refund-policy">Returns &amp; Refunds</Link></li>
              <li><Link to="/#faq" className="text-sm text-neutral-400 transition-colors hover:text-white" data-testid="footer-faq-link">FAQs</Link></li>
              <li><a href="mailto:everydayessentials@gmail.com" className="text-sm text-neutral-400 transition-colors hover:text-white" data-testid="footer-contact-link">everydayessentials@gmail.com</a></li>
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500">About</h3>
            <ul className="mt-4 space-y-2.5">
              <li><Link to="/our-story" className="text-sm text-neutral-400 transition-colors hover:text-white" data-testid="footer-our-story">Our Story</Link></li>
              <li><Link to="/about" className="text-sm text-neutral-400 transition-colors hover:text-white">Quality Commitment</Link></li>
              <li><Link to="/policies/privacy" className="text-sm text-neutral-400 transition-colors hover:text-white" data-testid="footer-privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/policies/terms" className="text-sm text-neutral-400 transition-colors hover:text-white" data-testid="footer-terms">Terms &amp; Conditions</Link></li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-3 border-t border-white/10 py-7 sm:flex-row">
          <p className="text-xs text-neutral-500">© 2026 Everyday Essentials. All rights reserved. Crafted for effortless living.</p>
          <p className="text-xs text-neutral-500">UPI · Cards · Net Banking · EMI · COD</p>
        </div>
      </div>
    </footer>
  );
}
